import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SectionHeading } from "../components/SectionHeading";
import api, { formatApiError } from "../lib/api";
import { BRAND, EVENT_TYPES, SERVICE_OPTIONS, BUDGETS, EVENTS } from "../data/content";
import { useMenu } from "../hooks/useMenu";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-charcoal">{label}{required && <span className="text-crimson"> *</span>}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const inputCls = "w-full min-h-[48px] rounded-xl border border-slate-300 bg-white px-4 py-3 text-charcoal outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 transition";

export default function Contact() {
  const [tab, setTab] = useState("quote");
  return (
    <div data-testid="contact-page" className="bg-bone">
      <section className="pt-28 lg:pt-36 pb-16 bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="eyebrow text-gold mb-3">Contact</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-white text-balance max-w-3xl">
            Let's Create Something Delicious.
          </h1>
          <div className="mt-8 flex flex-wrap gap-6 text-platinum">
            <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 hover:text-gold"><Mail className="w-4 h-4 text-gold" />{BRAND.email}</a>
            <a href={BRAND.instagramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-gold"><Instagram className="w-4 h-4 text-gold" />@{BRAND.instagram}</a>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gold" />{BRAND.serviceArea}</span>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 mb-8 rounded-full bg-white border border-slate-200 p-1 w-full sm:w-fit">
            <button data-testid="contact-tab-quote" onClick={() => setTab("quote")}
              className={`flex-1 sm:flex-none rounded-full px-6 py-2.5 text-sm font-semibold transition ${tab==="quote"?"bg-gold text-charcoal":"text-slate-600"}`}>
              Request a Quote
            </button>
            <button data-testid="contact-tab-general" onClick={() => setTab("general")}
              className={`flex-1 sm:flex-none rounded-full px-6 py-2.5 text-sm font-semibold transition ${tab==="general"?"bg-gold text-charcoal":"text-slate-600"}`}>
              General Inquiry
            </button>
          </div>
          {tab === "quote" ? <QuoteForm /> : <GeneralForm />}
        </div>
      </section>
    </div>
  );
}

function QuoteForm() {
  const menu = useMenu();
  const allDishes = Object.values(menu).flat();
  const [f, setF] = useState({
    name: "", email: "", phone: "", event_type: "", event_date: "", event_time: "",
    location: "", guest_count: "", services: [], menu_interests: [], custom_dish: "", budget: "", details: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone) { toast.error("Please fill in your contact details."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/catering-inquiries", f);
      setDone(data.request_id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid="quote-confirmation" className="rounded-3xl bg-white border border-slate-200 p-10 text-center">
        <CheckCircle2 className="w-14 h-14 text-emerald mx-auto" />
        <h2 className="mt-5 font-serif text-3xl text-charcoal">Thank You for Contacting Taste of Platinum!</h2>
        <p className="mt-3 text-slate-600">We've received your request and look forward to learning more about your event. A member of our team will review your information and follow up with next steps.</p>
        <div className="mt-6 inline-block rounded-xl bg-bone px-6 py-3">
          <span className="text-sm text-slate-500">Reference ID</span>
          <div className="font-serif text-2xl text-charcoal" data-testid="quote-reference-id">{done}</div>
        </div>
        <div className="mt-8"><Link to="/" className="text-charcoal font-semibold border-b-2 border-gold pb-1">Return Home</Link></div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quote-form" className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-9 space-y-8">
      <SectionHeading title="Request a Custom Quote" subtitle="Tell us about your event so we can craft the perfect menu and pricing." />

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Name" required><input data-testid="quote-name" className={inputCls} value={f.name} onChange={(e)=>set("name",e.target.value)} /></Field>
        <Field label="Email" required><input data-testid="quote-email" type="email" className={inputCls} value={f.email} onChange={(e)=>set("email",e.target.value)} /></Field>
        <Field label="Phone Number" required><input data-testid="quote-phone" className={inputCls} value={f.phone} onChange={(e)=>set("phone",e.target.value)} /></Field>
        <Field label="Event Type">
          <select data-testid="quote-event-type" className={inputCls} value={f.event_type} onChange={(e)=>set("event_type",e.target.value)}>
            <option value="">Select…</option>
            {EVENT_TYPES.map((t)=><option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Event Date"><input data-testid="quote-event-date" type="date" className={inputCls} value={f.event_date} onChange={(e)=>set("event_date",e.target.value)} /></Field>
        <Field label="Event Start Time"><input data-testid="quote-event-time" type="time" className={inputCls} value={f.event_time} onChange={(e)=>set("event_time",e.target.value)} /></Field>
        <Field label="Event Location / City"><input data-testid="quote-location" className={inputCls} value={f.location} onChange={(e)=>set("location",e.target.value)} placeholder="e.g. Atlanta, GA" /></Field>
        <Field label="Estimated Guest Count"><input data-testid="quote-guests" type="number" min="1" className={inputCls} value={f.guest_count} onChange={(e)=>set("guest_count",e.target.value)} /></Field>
      </div>

      <div>
        <span className="text-sm font-medium text-charcoal">Service Requested</span>
        <div className="mt-3 flex flex-wrap gap-2">
          {SERVICE_OPTIONS.map((s)=>(
            <button type="button" key={s} data-testid={`quote-service-${s.toLowerCase().replace(/\s+/g,'-')}`} onClick={()=>toggle("services",s)}
              className={`rounded-full px-4 py-2 text-sm border transition ${f.services.includes(s)?"bg-charcoal text-white border-charcoal":"bg-white text-slate-600 border-slate-300 hover:border-gold"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className="text-sm font-medium text-charcoal">Menu Interest</span>
        <p className="text-sm text-slate-500 mt-1">Please select or list ALL dishes you're interested in so we can provide the most accurate quote possible.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {allDishes.map((d)=>(
            <button type="button" key={d} data-testid={`quote-dish-${d.toLowerCase().replace(/\s+/g,'-').replace(/&/g,'and')}`} onClick={()=>toggle("menu_interests",d)}
              className={`rounded-full px-3.5 py-1.5 text-sm border transition ${f.menu_interests.includes(d)?"bg-gold text-charcoal border-gold":"bg-white text-slate-600 border-slate-300 hover:border-gold"}`}>
              {d}
            </button>
          ))}
        </div>
        <div className="mt-4">
          <Field label="Custom Dish Request">
            <textarea data-testid="quote-custom-dish" rows={2} className={inputCls} placeholder="Don't see the dish you're looking for? Let us know what you'd like." value={f.custom_dish} onChange={(e)=>set("custom_dish",e.target.value)} />
          </Field>
        </div>
      </div>

      <Field label="Budget (optional)">
        <select data-testid="quote-budget" className={inputCls} value={f.budget} onChange={(e)=>set("budget",e.target.value)}>
          <option value="">Prefer not to say</option>
          {BUDGETS.map((b)=><option key={b} value={b}>{b}</option>)}
        </select>
      </Field>

      <Field label="Additional Details">
        <textarea data-testid="quote-details" rows={4} className={inputCls} placeholder="Tell us more about your event, vision, menu preferences, and any special requests." value={f.details} onChange={(e)=>set("details",e.target.value)} />
      </Field>

      <button type="submit" data-testid="quote-submit" disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 font-semibold text-charcoal hover:bg-gold-bright disabled:opacity-60">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Submit Quote Request"}
      </button>
    </form>
  );
}

function GeneralForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) { toast.error("Please fill in all required fields."); return; }
    setSubmitting(true);
    try {
      await api.post("/contact", f);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid="general-confirmation" className="rounded-3xl bg-white border border-slate-200 p-10 text-center">
        <CheckCircle2 className="w-14 h-14 text-emerald mx-auto" />
        <h2 className="mt-5 font-serif text-3xl text-charcoal">Message Received!</h2>
        <p className="mt-3 text-slate-600">Thank you for reaching out. A member of our team will follow up with you soon.</p>
        <div className="mt-8"><Link to="/" className="text-charcoal font-semibold border-b-2 border-gold pb-1">Return Home</Link></div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="general-form" className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-9 space-y-6">
      <SectionHeading title="Send Us a Message" subtitle="Have a general question? We'd love to hear from you." />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Name" required><input data-testid="general-name" className={inputCls} value={f.name} onChange={(e)=>set("name",e.target.value)} /></Field>
        <Field label="Email" required><input data-testid="general-email" type="email" className={inputCls} value={f.email} onChange={(e)=>set("email",e.target.value)} /></Field>
      </div>
      <Field label="Phone Number"><input data-testid="general-phone" className={inputCls} value={f.phone} onChange={(e)=>set("phone",e.target.value)} /></Field>
      <Field label="Message" required><textarea data-testid="general-message" rows={5} className={inputCls} value={f.message} onChange={(e)=>set("message",e.target.value)} /></Field>
      <button type="submit" data-testid="general-submit" disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 font-semibold text-charcoal hover:bg-gold-bright disabled:opacity-60">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Send Message"}
      </button>
    </form>
  );
}
